// 비밀번호가 걸린 PDF 를 클라이언트에서 검증/복호화한다.
// pdfjs-dist 는 첫 사용 시점에만 dynamic import 해서 메인 번들 영향을 줄인다.
// BE 분석기는 암호 dictionary 가 남은 PDF (incremental save 결과 포함) 를 거부하므로
// 복호화 후엔 페이지들을 세로로 이어 붙인 단일 JPEG 으로 변환해 image/jpeg 업로드 경로로 보낸다.

type PdfPasswordInspection =
  | { kind: 'no-password' }
  | { kind: 'needs-password' }
  | { kind: 'corrupt' };

type PdfDecryptResult =
  | { ok: true; file: File }
  | { ok: false; reason: 'incorrect' | 'corrupt' };

let pdfjsModulePromise: Promise<typeof import('pdfjs-dist')> | null = null;

const loadPdfjs = () => {
  if (!pdfjsModulePromise) {
    pdfjsModulePromise = import('pdfjs-dist').then((mod) => {
      if (typeof window !== 'undefined' && !mod.GlobalWorkerOptions.workerSrc) {
        mod.GlobalWorkerOptions.workerSrc = new URL(
          'pdfjs-dist/build/pdf.worker.min.mjs',
          import.meta.url,
        ).toString();
      }
      return mod;
    });
  }
  return pdfjsModulePromise;
};

const isPasswordException = (e: unknown): boolean =>
  typeof e === 'object' && e !== null && (e as { name?: string }).name === 'PasswordException';

export const inspectPdfPassword = async (
  file: File,
): Promise<PdfPasswordInspection> => {
  const pdfjs = await loadPdfjs();
  let task: import('pdfjs-dist').PDFDocumentLoadingTask | null = null;
  let doc: import('pdfjs-dist').PDFDocumentProxy | null = null;
  try {
    const buffer = await file.arrayBuffer();
    task = pdfjs.getDocument({ data: new Uint8Array(buffer) });
    doc = await task.promise;
    return { kind: 'no-password' };
  } catch (e) {
    if (isPasswordException(e)) return { kind: 'needs-password' };
    return { kind: 'corrupt' };
  } finally {
    // doc.destroy() 가 내부 loadingTask 까지 정리하므로 doc 이 있으면 doc 만,
    // task.promise 단계에서 실패해 doc 이 없으면 task 를 정리한다.
    if (doc) {
      await doc.destroy();
    } else if (task) {
      await task.destroy();
    }
  }
};

const replaceExtension = (name: string, ext: string) => {
  const dot = name.lastIndexOf('.');
  return (dot < 0 ? name : name.slice(0, dot)) + ext;
};

const rasterizeDocumentToJpeg = async (
  doc: import('pdfjs-dist').PDFDocumentProxy,
  filename: string,
): Promise<File> => {
  // BE OCR 정확도를 위해 ~288 DPI (PDF 기본 72 DPI × 4) 로 렌더링.
  // 더 낮추면 작은 폰트(영수증 항목) OCR 인식률이 떨어짐.
  const scale = 4;
  const pageCanvases: HTMLCanvasElement[] = [];

  for (let i = 1; i <= doc.numPages; i += 1) {
    const page = await doc.getPage(i);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas context unavailable');

    await page.render({
      canvas,
      canvasContext: ctx,
      viewport,
    }).promise;

    pageCanvases.push(canvas);
    page.cleanup();
  }

  const combinedWidth = pageCanvases.reduce((max, c) => Math.max(max, c.width), 0);
  const combinedHeight = pageCanvases.reduce((sum, c) => sum + c.height, 0);

  const combined = document.createElement('canvas');
  combined.width = combinedWidth;
  combined.height = combinedHeight;
  const combinedCtx = combined.getContext('2d');
  if (!combinedCtx) throw new Error('canvas context unavailable');
  combinedCtx.fillStyle = '#ffffff';
  combinedCtx.fillRect(0, 0, combinedWidth, combinedHeight);

  let yOffset = 0;
  for (const pageCanvas of pageCanvases) {
    combinedCtx.drawImage(pageCanvas, 0, yOffset);
    yOffset += pageCanvas.height;
  }

  const blob: Blob = await new Promise((resolve, reject) => {
    combined.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('canvas toBlob failed'))),
      'image/jpeg',
      0.95,
    );
  });

  return new File([blob], replaceExtension(filename, '.jpg'), {
    type: 'image/jpeg',
  });
};

export const tryDecryptPdfWithPassword = async (
  file: File,
  password: string,
): Promise<PdfDecryptResult> => {
  const pdfjs = await loadPdfjs();
  let task: import('pdfjs-dist').PDFDocumentLoadingTask | null = null;
  let doc: import('pdfjs-dist').PDFDocumentProxy | null = null;
  try {
    const buffer = await file.arrayBuffer();
    task = pdfjs.getDocument({
      data: new Uint8Array(buffer),
      password,
    });
    doc = await task.promise;
    const image = await rasterizeDocumentToJpeg(doc, file.name);
    return { ok: true, file: image };
  } catch (e) {
    if (isPasswordException(e)) return { ok: false, reason: 'incorrect' };
    return { ok: false, reason: 'corrupt' };
  } finally {
    // doc.destroy() 가 내부 loadingTask 까지 정리하므로 doc 이 있으면 doc 만,
    // task.promise 단계에서 실패해 doc 이 없으면 task 를 정리한다.
    if (doc) {
      await doc.destroy();
    } else if (task) {
      await task.destroy();
    }
  }
};
